import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../../services/api";
import {
  clearAuthUser,
  getStoredAuthUser,
  normalizeAuthUser,
  storeAuthUser,
} from "../../utils/authUser";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [loading, setLoading] = useState(true);

  const setAuthUser = (nextUser) => {
    const normalized = normalizeAuthUser(nextUser) || nextUser || null;
    setUser(normalized);
    storeAuthUser(normalized);
  };

  const fetchProfile = async () => {
    const res = await api.get("/auth/profile");
    const profile = normalizeAuthUser(res.data);
    if (profile) {
      const storedUser = getStoredAuthUser();
      setAuthUser({
        ...profile,
        companyId: profile.companyId || storedUser?.companyId || null,
      });
    }
    return profile;
  };

  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      setIsAuthenticated(true);
      const stored = getStoredAuthUser();
      if (stored) setUser(stored);

      try {
        await fetchProfile();
      } catch {
        // Keep stored profile if the request fails.
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async ({ accessToken, refreshToken, user: nextUser } = {}) => {
    if (accessToken) localStorage.setItem("accessToken", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    if (nextUser) setAuthUser(nextUser);
    setIsAuthenticated(true);

    try {
      await fetchProfile();
    } catch {
      // Login still succeeds with the payload we already saved.
    }

    return true;
  };

  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearAuthUser();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (nextUser) => {
    setAuthUser(nextUser);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    updateUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
